import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { webApi, errText, fmtDate } from "@/lib/webApi";
import { PageHead, primary, ghost, useMe } from "@/pages/web/WebApp";
import { StorageBar } from "@/pages/web/WebMatters";

const HOW = { auto_renew: "Auto-renew", one_time: "One-time payment", manual: "Set by the Dicta team", free: "Free", complimentary: "No limits, no charges. Set by the Dicta team" };

export function Billing() {
  const [b, setB] = useState(null);
  const load = useCallback(() => webApi.get("/billing").then((r) => setB(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!b) return null;
  const pay = async (opt) => {
    const w = window.open("", "_blank");
    try {
      const { data } = await webApi.post("/billing/link", { opt });
      if (w) w.location = data.url; else window.location = data.url;
    } catch (e) { if (w) w.close(); toast.error(errText(e)); }
  };
  const cancel = async () => {
    if (!window.confirm("Stop auto-renewal? Your plan stays on until the end of the paid period.")) return;
    const { data } = await webApi.post("/billing/cancel");
    toast.message(data.message.split("\n")[0]);
    load();
  };
  const update = async () => {
    try { const { data } = await webApi.post("/billing/update-payment"); window.open(data.url, "_blank"); } catch (e) { toast.error(errText(e)); }
  };
  const end = b.manual_until || b.paid_until;
  return (
    <div data-testid="web-billing">
      <PageHead title="Plan & billing" />
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-white/10 bg-[#0E121A] p-5">
          <p className="text-xs uppercase tracking-wide text-slate-500">Current plan</p>
          <p className="mt-1 text-2xl font-semibold text-slate-100" data-testid="billing-plan">{b.plan_label}</p>
          {b.how !== "free" && <p className="mt-1 text-sm text-slate-400" data-testid="billing-how">{HOW[b.how]}{b.status && b.how !== "complimentary" ? ` · ${b.cancel_requested && b.status === "active" ? "cancelled" : b.status.replace("_", " ")}` : ""}</p>}
          {end && <p className="mt-1 text-sm text-slate-400">{b.how === "auto_renew" && !b.cancel_requested ? "Renews on" : "Ends on"} {fmtDate(end)}</p>}
          <div className="mt-4"><StorageBar storage={b.storage} /></div>
          <div className="mt-4 flex flex-wrap gap-2">
            {b.how === "auto_renew" && !b.cancel_requested && b.how !== "complimentary" && <button onClick={cancel} className={ghost} data-testid="billing-cancel-button">Cancel auto-renewal</button>}
            {b.status === "retrying" && <button onClick={update} className={primary} data-testid="billing-update-payment-button">Update payment</button>}
          </div>
        </div>
        {b.options.length > 0 && <div className="rounded-lg border border-white/10 bg-[#0E121A] p-5">
          <p className="mb-3 text-xs uppercase tracking-wide text-slate-500">Upgrade or pay once (prices include taxes)</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {b.options.map((o) => (
              <button key={o.id} onClick={() => pay(o.id)} data-testid={`billing-option-${o.id}`}
                className="rounded-md border border-white/10 p-3 text-left transition-colors duration-200 hover:border-amber-300/50">
                <p className="text-sm font-medium text-slate-100">{o.title}</p>
                <p className="text-xs text-slate-500">{o.desc}</p>
              </button>
            ))}
          </div>
        </div>}
      </div>
      <h2 className="mb-3 mt-8 text-base font-semibold text-slate-200 md:text-lg">Payment history</h2>
      <div className="rounded-lg border border-white/10" data-testid="billing-history">
        {b.payments.length === 0 && <p className="p-4 text-sm text-slate-500">No payments yet.</p>}
        {b.payments.map((p) => (
          <div key={p.id} className="flex justify-between border-t border-white/5 px-4 py-2 text-sm first:border-0">
            <span>{(b.options.find((o) => o.id === p.opt) || {}).title || p.plan}</span>
            <span className="font-mono-data">₹{((p.amount || 0) / 100).toLocaleString("en-IN")}</span>
            <span className="text-slate-500">{p.status}</span>
            <span className="text-slate-500">{fmtDate(p.created_at)}</span>
          </div>
        ))}
      </div>
      <p className="mt-6 flex flex-wrap gap-4 text-sm">
        {Object.entries(b.links).map(([k, url]) => <a key={k} href={url} target="_blank" rel="noreferrer" className="capitalize text-slate-400 hover:text-white" data-testid={`billing-link-${k}`}>{k === "refunds" ? "Cancellation & Refunds" : k}</a>)}
      </p>
    </div>
  );
}

export function Account() {
  const nav = useNavigate();
  const { reload } = useMe();
  const [a, setA] = useState(null);
  const [form, setForm] = useState({ name: "", advocate_name: "" });
  const load = useCallback(() => webApi.get("/account").then((r) => { setA(r.data); setForm({ name: r.data.name || "", advocate_name: r.data.advocate_name || "" }); }), []);
  useEffect(() => { load(); }, [load]);
  if (!a) return null;
  const save = async () => { await webApi.patch("/account", form); toast.success("Saved"); reload(); };
  const signOut = async (all) => {
    await webApi.post(all ? "/auth/signout-all" : "/auth/signout");
    nav("/app/login", { replace: true });
  };
  const exportData = async () => {
    try { const { data } = await webApi.post("/account/export"); window.open(data.url, "_blank"); } catch (e) { toast.error(errText(e)); }
  };
  const del = async () => {
    if (!window.confirm("Start deleting all your Dicta data? You'll confirm on WhatsApp.")) return;
    try { const { data } = await webApi.post("/account/delete"); toast.message(data.message); } catch (e) { toast.error(errText(e)); }
  };
  const field = "w-full rounded-md border border-white/10 bg-[#07090D] px-3 py-2 text-sm";
  return (
    <div data-testid="web-account">
      <PageHead title="Account" />
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-3 rounded-lg border border-white/10 bg-[#0E121A] p-5">
          <label className="block text-xs text-slate-500">Name<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} data-testid="account-name-input" /></label>
          <label className="block text-xs text-slate-500">Advocate name<input value={form.advocate_name} onChange={(e) => setForm({ ...form, advocate_name: e.target.value })} className={field} data-testid="account-advocate-input" /></label>
          <button onClick={save} className={primary} data-testid="account-save-button">Save</button>
        </div>
        <div className="rounded-lg border border-white/10 bg-[#0E121A] p-5">
          <p className="text-xs uppercase tracking-wide text-slate-500">WhatsApp numbers</p>
          {a.numbers.map((n, i) => <p key={i} className="mt-2 font-mono-data text-sm" data-testid={`account-number-${i}`}>{n.masked}{n.current && <span className="ml-2 text-xs text-slate-500">(signed in)</span>}</p>)}
          <p className="mt-2 text-xs text-slate-500">Link or unlink numbers on WhatsApp.</p>
        </div>
      </div>
      <div className="mt-4 rounded-lg border border-white/10 bg-[#0E121A] p-5" data-testid="account-sessions">
        <p className="text-xs uppercase tracking-wide text-slate-500">Active sessions</p>
        {a.sessions.map((s) => <p key={s.id} className="mt-2 text-sm">{s.device} <span className="text-slate-500">· last used {fmtDate(s.last_used)}</span>{s.current && <span className="ml-2 text-xs text-amber-300">this browser</span>}</p>)}
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => signOut(false)} className={ghost} data-testid="sign-out-button"><LogOut className="h-4 w-4" /> Sign out</button>
          <button onClick={() => signOut(true)} className={ghost} data-testid="sign-out-everywhere-button"><LogOut className="h-4 w-4" /> Sign out everywhere</button>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={exportData} className={ghost} data-testid="account-export-button"><Download className="h-4 w-4" /> MY DATA export</button>
        <button onClick={del} className={`${ghost} text-red-300`} data-testid="account-delete-button"><Trash2 className="h-4 w-4" /> Delete my data</button>
      </div>
    </div>
  );
}
